<script setup>
/**
 * The state pill as a control: shows the ticket's current state and opens a
 * menu of the four writable workflow states, each annotated with the system
 * category it maps to (Submitted / In progress / Waiting on customer /
 * Resolved). Shelved states (archive / spam / trash / closed) come from
 * `resolution`, which the move menu owns - the pill goes static for them.
 *
 * The write is a plain `toggleStatus` on this conversation only. State never
 * cascades across tracker links, so flipping a back-office or tracker ticket's
 * state can never reopen the linked conversations.
 */
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import { useStore } from 'dashboard/composables/store';

import StatusPill from 'dashboard/components-next/Conversation/StatusPill.vue';
import {
  CATEGORY_META,
  SENDABLE_STATES,
  STATE_CATEGORY,
  STATE_META,
  STATUS_FOR_STATE,
  deriveTicketState,
} from 'dashboard/helper/conversationLens';

const props = defineProps({
  conversation: { type: Object, required: true },
});

const { t } = useI18n();
const store = useStore();

const open = ref(false);

const state = computed(() => deriveTicketState(props.conversation));
const isWritable = computed(() => SENDABLE_STATES.includes(state.value));

const options = computed(() =>
  SENDABLE_STATES.map(value => ({
    value,
    label: t(STATE_META[value].labelKey),
    icon: STATE_META[value].icon,
    category: t(CATEGORY_META[STATE_CATEGORY[value]].labelKey),
    active: value === state.value,
  }))
);

const setState = value => {
  open.value = false;
  if (value === state.value) return;
  store.dispatch('toggleStatus', {
    conversationId: props.conversation.id,
    status: STATUS_FOR_STATE[value],
  });
};
</script>

<template>
  <div v-on-click-outside="() => (open = false)" class="relative inline-flex">
    <StatusPill v-if="!isWritable" :state="state" />
    <button
      v-else
      type="button"
      data-testid="state-pill-trigger"
      aria-haspopup="menu"
      :aria-expanded="open"
      :aria-label="t('TICKETS.DETAIL.CHANGE_STATE')"
      class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full"
      :class="STATE_META[state].pillClass"
      @click="open = !open"
    >
      <span :class="STATE_META[state].icon" class="size-3 shrink-0" />
      {{ t(STATE_META[state].labelKey) }}
      <span class="i-lucide-chevron-down size-3 shrink-0" />
    </button>

    <div
      v-if="open"
      role="menu"
      class="absolute z-20 w-56 p-1 rounded-xl top-7 start-0 bg-n-solid-1 outline outline-1 -outline-offset-1 outline-n-container shadow-lg"
    >
      <button
        v-for="option in options"
        :key="option.value"
        type="button"
        role="menuitemradio"
        :aria-checked="option.active"
        :data-testid="`state-option-${option.value}`"
        class="flex items-center w-full h-8 gap-2 px-2.5 text-sm rounded-lg text-start text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12"
        @click="setState(option.value)"
      >
        <span :class="option.icon" class="size-4 shrink-0" />
        {{ option.label }}
        <span class="ms-auto text-[11px] text-n-slate-10">
          {{ option.category }}
        </span>
        <span
          v-if="option.active"
          class="i-lucide-check size-3.5 shrink-0 text-n-slate-11"
        />
      </button>
    </div>
  </div>
</template>
